<template>
  <MetaInfo
    :title="meta.title"
    :description="meta.description"
  ></MetaInfo>

  <div class="support-page">
    <navigation></navigation>

    <!-- Left Sidebar -->
    <support-sidebar></support-sidebar>

    <div class="support-content">
      <div class="support-content__wrapper">
        <div class="support-content__main">
          <!-- Error Message -->
          <div v-if="error" class="support-error">
            <h3>Error Loading Article</h3>
            <p><strong>{{ error.message }}</strong></p>
            <p v-if="error.isAuthError">
              Please check that your HelpScout API key is correctly configured in your <code>.env.local</code> file.
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="loading && !error">
            <skeleton-loader type="article"></skeleton-loader>
          </div>

          <!-- Article Content -->
          <div v-if="!loading && !error && article" class="support-article">
            <div class="support-article__header">
              <router-link
                v-if="collectionName"
                :to="`/support/${$route.params.collectionId}`"
                class="support-article__collection-link"
              >
                {{ collectionName }}
              </router-link>
              <h1>{{ article.name }}</h1>
            </div>

            <!-- Article Text/HTML -->
            <div 
              v-if="article.text" 
              v-html="article.text" 
              class="support-article__content"
            ></div>

            <!-- Fallback if no text -->
            <div v-else class="support-article__no-content">
              <p>Article content is not available.</p>
              <p v-if="article.publicUrl">
                <a :href="article.publicUrl" target="_blank">
                  View this article on HelpScout →
                </a>
              </p>
            </div>

            <!-- Related Articles -->
            <div v-if="relatedArticles.length > 0" class="support-article__related">
              <h3>Related Articles</h3>
              <ul class="support-article__related-list">
                <li v-for="relatedArticle in relatedArticles" :key="relatedArticle.id">
                  <router-link
                    :to="`/support/${$route.params.collectionId}/${relatedArticle.slug || relatedArticle.id}`"
                  >
                    {{ relatedArticle.name }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>

          <!-- Article not found -->
          <div v-if="!loading && !error && !article" class="support-empty">
            <h1>Article Not Found</h1>
            <p>The requested article could not be found.</p>
            <router-link to="/support">
              ← Back to all collections
            </router-link>
          </div>
        </div>

        <!-- Right Sidebar (TOC) - Inside content area -->
        <article-table-of-contents 
          v-if="article && article.text"
          :article-html="article.text"
        ></article-table-of-contents>
      </div>
    </div>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SupportSidebar from "@/components/Support/SupportSidebar";
import ArticleTableOfContents from "@/components/Support/ArticleTableOfContents";
import SkeletonLoader from "@/components/Support/SkeletonLoader";
import helpScoutService from "@/services/helpscout";
import helpScoutState from "@/services/helpscoutState";

export default {
  name: "SupportArticlePage",
  components: {
    MetaInfo,
    Navigation,
    SupportSidebar,
    ArticleTableOfContents,
    SkeletonLoader,
  },
  data() {
    return {
      article: null,
      collection: null,
      relatedArticles: [],
      loading: true,
      error: null,
      debugData: null,
      meta: {
        title: "",
        description: "",
      },
    };
  },
  async created() {
    await this.loadArticle();
  },
  watch: {
    '$route.params': {
      handler() {
        this.loadArticle();
      },
    },
    article: {
      handler(newArticle) {
        if (newArticle && newArticle.text) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.enhanceHeadings();
              this.scrollToHashIfPresent();
            }, 100);
          });
        }
      },
    },
  },
  computed: {
    collectionName() {
      if (this.collection && this.collection.name) {
        return this.collection.name;
      }
      const fromState = helpScoutState.findCollection(this.$route.params.collectionId);
      return fromState?.name || '';
    },
  },
  methods: {
    async loadArticle() {
      try {
        this.loading = true;
        this.error = null;
        this.article = null;
        this.collection = null;
        this.relatedArticles = [];
        this.meta.title = "";
        this.meta.description = "";

        const collectionIdentifier = this.$route.params.collectionId;
        const articleIdentifier = this.$route.params.articleId;

        // Ensure shared collections state is populated (cold-start: direct deep link)
        await helpScoutState.getCollections(helpScoutService);

        // Use shared state to find the collection
        const foundCollection = helpScoutState.findCollection(collectionIdentifier);

        if (foundCollection) {
          // Optimized parallel fetching strategy
          let foundArticleId = null;

          // Run multiple searches in parallel for faster results
          const searchPromises = [];

          // Promise 1: Get full collection details (always needed)
          const collectionPromise = helpScoutService.getCollection(foundCollection.id)
            .then(collectionDetail => {
              if (collectionDetail && collectionDetail.collection) {
                this.collection = collectionDetail.collection;
              }
            })
            .catch(err => console.warn('Collection detail fetch failed:', err));
          
          searchPromises.push(collectionPromise);

          // Promise 2: If articleIdentifier looks like a direct ID, try fetching it
          if (articleIdentifier && !articleIdentifier.includes('-')) {
            console.log('Attempting parallel fetch: direct article by ID');
            const directArticlePromise = helpScoutService.getArticle(articleIdentifier)
              .then(articleData => {
                if (articleData && articleData.article && articleData.article.status === 'published') {
                  return { found: true, articleId: articleIdentifier, article: articleData.article, method: 'direct' };
                }
                return { found: false };
              })
              .catch(() => ({ found: false }));
            
            searchPromises.push(directArticlePromise);
          }

          // Promise 3: Fetch all articles from collection
          console.log('Attempting parallel fetch: all collection articles');
          const collectionArticlesPromise = helpScoutService.getArticles(foundCollection.id)
            .then(articlesData => {
              if (Array.isArray(articlesData)) {
                const foundInCollection = articlesData.find(
                  a => a.id === articleIdentifier || a.slug === articleIdentifier
                );
                
                if (foundInCollection) {
                  return { found: true, articleId: foundInCollection.id, method: 'collection' };
                }
              }
              return { found: false };
            })
            .catch(() => ({ found: false }));
          
          searchPromises.push(collectionArticlesPromise);

          // Wait for all parallel searches to complete
          const results = await Promise.allSettled(searchPromises);
          
          // Find first successful result (skip the collection detail promise)
          const articleResult = results
            .slice(1) // Skip collection detail promise
            .filter(r => r.status === 'fulfilled')
            .map(r => r.value)
            .find(r => r.found);

          if (articleResult) {
            console.log(`Article found via ${articleResult.method} method`);
            foundArticleId = articleResult.articleId;
            if (articleResult.article) {
              this.article = articleResult.article;
            }
          }

          // Fallback: If still not found, search through categories (optimized)
          if (!foundArticleId) {
            console.log('Article not found in collection, searching categories...');
            
            // Use shared state to get categories (with caching)
            const categories = await helpScoutState.getCategories(foundCollection.id, helpScoutService);
            
            // Search categories in parallel, but use ONLY getArticlesByCategory (skip getCategory)
            const categorySearches = categories.map(async (category) => {
              try {
                const categoryArticlesData = await helpScoutService.getArticlesByCategory(category.id);
                if (Array.isArray(categoryArticlesData)) {
                  const foundInCategory = categoryArticlesData.articles.items.find(
                    a => a.id === articleIdentifier || a.slug === articleIdentifier
                  );
                  
                  if (foundInCategory) {
                    return { found: true, articleId: foundInCategory.id, category: category.name };
                  }
                }
                return { found: false };
              } catch (err) {
                return { found: false };
              }
            });

            // Wait for all category searches with graceful error handling
            const categoryResults = await Promise.allSettled(categorySearches);
            
            const successResult = categoryResults
              .filter(r => r.status === 'fulfilled')
              .map(r => r.value)
              .find(r => r.found);
              
            if (successResult) {
              console.log(`Found article in category "${successResult.category}"`);
              foundArticleId = successResult.articleId;
            }
          }

          // If we found the article ID, fetch its full details
          if (foundArticleId && !this.article) {
            console.log('Fetching full article details for ID:', foundArticleId);
            const articleData = await helpScoutService.getArticle(foundArticleId);
            
            if (articleData) {
                this.article = articleData;
            }
          }

          // If we have an article, update meta and fetch related articles in parallel
          if (this.article) {
            console.log('Article loaded successfully:', this.article.name);
            
            // Update meta (synchronous)
            this.meta.title = `${this.article.name} - Support`;
            this.meta.description =
              this.article.preview ||
              `${this.article.name} - Transpond support documentation.`;

            // Fetch related articles in background (non-blocking)
            // Don't await - let it load asynchronously
            helpScoutService.getRelatedArticles(foundArticleId || this.article.id)
              .then(relatedData => {
                if (Array.isArray(relatedData)) {
                  this.relatedArticles = relatedData;
                }
              })
              .catch(err => console.warn('Could not load related articles:', err));
          } else {
            console.error('Article not found. Searched for identifier:', articleIdentifier);
            this.meta.title = "Support Documentation";
            this.meta.description =
              "Browse our support documentation and help articles.";
          }

          this.debugData = {
            collection: this.collection,
            article: this.article,
            relatedArticles: this.relatedArticles,
          };
        } else {
          this.meta.title = "Support Documentation";
          this.meta.description =
            "Browse our support documentation and help articles.";
        }

      } catch (error) {
        console.error('Error loading article:', error);
        
        const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);
        
        this.error = {
          message: error.message || 'Failed to load article',
          isAuthError,
          details: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : null
        };

        this.debugData = { error: this.error };
        if (!this.meta.title) {
          this.meta.title = "Support Documentation";
        }
        if (!this.meta.description) {
          this.meta.description =
            "Browse our support documentation and help articles.";
        }
      } finally {
        this.loading = false;
      }
    },
    slugify(text) {
      return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    scrollContainerToElement(element) {
      const scrollContainer = document.querySelector('.support-content');
      if (!element || !scrollContainer) return;
      const offset = 20;
      scrollContainer.scrollTo({
        top: element.offsetTop - offset,
        behavior: 'smooth',
      });
    },
    enhanceHeadings() {
      const content = document.querySelector('.support-article__content');
      if (!content) return;

      const headings = content.querySelectorAll('h2, h3');
      const usedSlugs = new Set();

      headings.forEach((heading) => {
        if (heading.querySelector('.support-article__heading-anchor')) {
          if (heading.id) usedSlugs.add(heading.id);
          return;
        }

        let slug = this.slugify(heading.textContent || '');
        if (!slug) slug = 'section';

        let unique = slug;
        let counter = 2;
        while (usedSlugs.has(unique)) {
          unique = `${slug}-${counter++}`;
        }
        usedSlugs.add(unique);
        heading.id = unique;

        const anchor = document.createElement('a');
        anchor.className = 'support-article__heading-anchor';
        anchor.href = `#${unique}`;
        anchor.setAttribute('aria-label', 'Link to this section');
        anchor.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
        anchor.addEventListener('click', (event) => this.onHeadingAnchorClick(event, unique));
        heading.insertBefore(anchor, heading.firstChild);
      });
    },
    onHeadingAnchorClick(event, slug) {
      event.preventDefault();
      const element = document.getElementById(slug);
      if (!element) return;

      history.replaceState(null, '', `#${slug}`);
      this.scrollContainerToElement(element);

      const anchor = event.currentTarget;
      anchor.setAttribute('data-clicked', 'true');
      setTimeout(() => anchor.removeAttribute('data-clicked'), 1500);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(window.location.href).catch(() => {});
      }
    },
    scrollToHashIfPresent() {
      const hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      const id = decodeURIComponent(hash.slice(1));
      const element = document.getElementById(id);
      if (element) this.scrollContainerToElement(element);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
  },
};
</script>

<style scoped>
/* Override global heading styles for article content */
.support-article__content :deep(h2),
.support-article__content :deep(.h2) {
  font-size: 26px !important;
  font-weight: 600 !important;
  margin-top: 25px !important;
  margin-bottom: 12px !important;
  font-family: inherit !important;
  position: relative;
  scroll-margin-top: 20px;
}

.support-article__content :deep(.support-article__heading-anchor) {
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--color-gray-200);
  border-radius: 10px;
  background: var(--color-white, #fff);
  color: var(--color-gray-600);
  opacity: 0;
  transition: opacity 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  text-decoration: none;
}

.support-article__content :deep(h2:hover .support-article__heading-anchor),
.support-article__content :deep(h3:hover .support-article__heading-anchor),
.support-article__content :deep(.support-article__heading-anchor:focus-visible) {
  opacity: 1;
}

.support-article__content :deep(.support-article__heading-anchor:hover) {
  opacity: 1 !important;
  border-color: var(--color-gray-500);
  color: var(--color-gray-900);
}

.support-article__content :deep(.support-article__heading-anchor[data-clicked="true"]) {
  opacity: 1 !important;
  border-color: var(--color-indigo-600);
  color: var(--color-indigo-600);
}

@media (max-width: 768px) {
  .support-article__content :deep(.support-article__heading-anchor) {
    opacity: 0.5;
  }
}

.support-article__content :deep(h3),
.support-article__content :deep(.h3) {
  font-size: 22px !important;
  font-weight: 600 !important;
  margin-top: 20px !important;
  margin-bottom: 10px !important;
  font-family: inherit !important;
  position: relative;
  scroll-margin-top: 20px;
}

.support-article__content :deep(h4),
.support-article__content :deep(.h4) {
  font-size: 16px !important;
  font-weight: 500 !important;
  margin-top: 20px !important;
  margin-bottom: 10px !important;
  font-family: inherit !important;
}

/* List styling overrides */
.support-article__content :deep(ul),
.support-article__content :deep(ol) {
  margin-bottom: 15px !important;
  padding-left: 30px !important;
  list-style-position: outside !important;
}

.support-article__content :deep(ul) {
  list-style-type: disc !important;
}

.support-article__content :deep(ol) {
  list-style-type: decimal !important;
}

.support-article__content :deep(li) {
  margin-bottom: 8px !important;
  line-height: 1.6 !important;
}

/* Nested lists */
.support-article__content :deep(ul ul) {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  list-style-type: circle !important;
}

.support-article__content :deep(ul ul ul) {
  list-style-type: square !important;
}

.support-article__content :deep(ol ol) {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  list-style-type: lower-alpha !important;
}

.support-article__content :deep(ol ol ol) {
  list-style-type: lower-roman !important;
}
</style>
